import React, {useCallback, useEffect, useRef, useState} from 'react'

import {Button, Flex, IconAddLine, Spinner, TopNavBar, View} from '@instructure/ui'
import EditTool from './EditTool'
import ListTools from './ListTools'
import ToolsApi from './api-remote'
import {BadCredentialsError} from './bad-credentials-error'
import CredentialsModal from './CredentialsModal'
import * as PropTypes from 'prop-types'
import {FloatingAlerts} from "./FloatingAlerts";
import {buildError, buildSuccess} from "./Alerts.jsx";
import {ltiRegistration, proxyRegistration} from "./registrations";
import {AppIcon} from "./AppIcon";

const tool = {
  id: null,
  sign: false,
  issuer: null,
  secret: null,
  lti: { ...ltiRegistration },
  proxy: { ...proxyRegistration },
}

const LAST_SERVER_URL_KEY = 'toolSupport:lastServerUrl'
const DEFAULT_SERVER_URL = import.meta.env.VITE_DEFAULT_SERVER_URL || 'https://localhost:18443'

function Loading(props) {
  return (props.show) ? <Spinner renderTitle='Loading' size='large' /> : props.children
}

Loading.propTypes = {
  show: PropTypes.bool,
  children: PropTypes.node
}

const App = () => {
  const [promptCredentials, setPromptCredentials] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [toolState, setToolState] = useState({ ...tool })
  const [tools, setTools] = useState([])
  const [alerts, setAlerts] = useState([])
  const [hasLoadedTools, setHasLoadedTools] = useState(false)
  const [loginError, setLoginError] = useState(null)
  const [serverUrl, setServerUrl] = useState(() => {
    try {
      return localStorage.getItem(LAST_SERVER_URL_KEY) || DEFAULT_SERVER_URL
    } catch {
      return DEFAULT_SERVER_URL
    }
  })
  const apiRef = useRef(new ToolsApi(serverUrl))

  useEffect(() => {
    apiRef.current.setUrl(serverUrl)
    try {
      localStorage.setItem(LAST_SERVER_URL_KEY, serverUrl)
    } catch {
      // ignore
    }
  }, [serverUrl])

  const loadTools = useCallback(async () => {
    setLoading(true)
    try {
      const loadedTools = await apiRef.current.loadTools()
      const sortedTools = [...loadedTools].sort((a, b) => {
        const aName = a?.lti?.clientName || ''
        const bName = b?.lti?.clientName || ''
        return aName.localeCompare(bName)
      })
      setTools(sortedTools)
      setHasLoadedTools(true)
      setPromptCredentials(false)
      setLoginError(null)
    } catch (error) {
      if (error instanceof BadCredentialsError) {
        setHasLoadedTools(false)
        setPromptCredentials(true)
        setLoginError(error.message || 'Login required')
      } else {
        const message = `Failed to load tools: ${error?.message || error}`
        if (!hasLoadedTools) {
          setPromptCredentials(true)
          setLoginError(message)
        } else {
          setAlerts((prev) => [...prev, buildError(message)])
        }
      }
    } finally {
      setLoading(false)
    }
  }, [hasLoadedTools])

  useEffect(() => {
    loadTools()
  }, [loadTools])

  const addTool = () => {
    setEditOpen(true)
    setToolState({ ...tool })
  }

  const editTool = (incomingTool) => {
    setEditOpen(true)
    setToolState({
      ...incomingTool
    })
  }

  const deleteTool = async (targetTool) => {
    if (confirm(`Are you sure you want to delete: ${targetTool.lti.registrationId}`)) {
      try {
        await apiRef.current.deleteTool(targetTool)
        const toolLabel = targetTool?.lti?.clientName || targetTool?.lti?.registrationId || 'tool'
        setAlerts((prev) => [...prev, buildSuccess(`Deleted ${toolLabel}`)])
        await loadTools()
      } catch (error) {
        if (error instanceof BadCredentialsError) {
          setPromptCredentials(true)
        } else {
          setAlerts((prev) => [...prev, buildError(`Failed to delete tool: ${error?.message || error}`)])
        }
      }
    }
  }

  const saveTool = async () => {
    const wasUpdate = Boolean(toolState?.id)
    const toolLabel = toolState?.lti?.clientName || toolState?.lti?.registrationId || 'tool'
    try {
      await apiRef.current.saveTool(toolState)
      setAlerts((prev) => [
        ...prev,
        buildSuccess(`${wasUpdate ? 'Updated' : 'Added'} ${toolLabel}`)
      ])
      setEditOpen(false)
      await loadTools()
    } catch (error) {
      if (error instanceof BadCredentialsError) {
        setPromptCredentials(true)
      } else {
        setAlerts((prev) => [...prev, buildError(`Failed to save tool: ${error?.message || error}`)])
      }
    }
  }

  const login = async (username, password, url) => {
    setLoginError(null)
    if (url) {
      setServerUrl(url)
      apiRef.current.setUrl(url)
    }
    await apiRef.current.credentials(username, password)
    setPromptCredentials(false)
    await loadTools()
  }

  const logout = () => {
    apiRef.current.credentials(null, null)
    setTools([])
    setHasLoadedTools(false)
    setLoginError(null)
    setEditOpen(false)
    setPromptCredentials(true)
  }

  const onChange = useCallback((updatedTool) => {
    setToolState((prev) => {
      return {...prev, ...updatedTool}
    })
  }, [])

  const dismissAlert = (alert) => {
    setAlerts((prevAlerts) => {
      return prevAlerts.filter((_, index) => index !== alert)
    })
  }

  return (
    <>
      <CredentialsModal open={promptCredentials} login={login} serverUrl={serverUrl} error={loginError} />
      <EditTool open={editOpen}
                onChange={onChange} value={toolState}
                onClose={() => setEditOpen(false)}
                onSave={saveTool}
      />
      <TopNavBar>
        {
          () => (
            <TopNavBar.Layout smallViewportConfig={{
              dropdownMenuToggleButtonLabel: 'Toggle Menu',
              dropdownMenuLabel: 'Main Menu'
            }} renderBrand={(
              <TopNavBar.Brand screenReaderLabel='Tool Support Admin' renderIcon={<AppIcon
                  size="small"
                  color="primary-inverse"
                  height="2.5rem"
                  width="2.5rem"
              />}
              />
            )} renderMenuItems={(
              <TopNavBar.MenuItems renderHiddenItemsMenuTriggerLabel={() => false}>
                <TopNavBar.Item id='tools'>
                  Tools
                </TopNavBar.Item>
              </TopNavBar.MenuItems>
            )} renderActionItems={hasLoadedTools ? (
              <TopNavBar.ActionItems renderHiddenItemsMenuTriggerLabel={() => 'More'}>
                <TopNavBar.Item
                  id='logout'
                  variant='button'
                  onClick={logout}
                >
                  Logout
                </TopNavBar.Item>
              </TopNavBar.ActionItems>
            ) : undefined} />
          )

        }
      </TopNavBar>
      <FloatingAlerts alerts={alerts} onDismiss={dismissAlert}/>

      <View as='div' margin='small'>
        <Loading show={loading}>
          <Flex justifyItems='end'>
            <Flex.Item>
              <Button color='primary' onClick={addTool}><IconAddLine /> Add Tool</Button>
            </Flex.Item>
          </Flex>
          <ListTools tools={tools} onEdit={editTool} onDelete={deleteTool} />
        </Loading>
      </View>
    </>
  )
}

export default App
